import { Dialog, Transition } from '@headlessui/react';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import Loader from 'common/components/Loader';
import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { useUpdateSubmissionCVMutation } from 'common/slices/submissionApi.slice';
import { cn } from 'common/utils';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { Fragment } from 'react';
import * as Yup from 'yup';

import CVModal from './CVModal';

const UpdateCVModal = ({ isOpen, setIsOpen, submissionSlug }) => {
  const [updateSubmissionCV, { isLoading }] = useUpdateSubmissionCVMutation();

  const toast = useToast();
  const { t } = useTranslation('offers\\details');

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleUpload = async (file) => {
    try {
      await updateSubmissionCV({
        submissionSlug: submissionSlug,
        cvFile: file,
      }).unwrap();
      toast.newToast(
        'positive',
        t('modals.upload-new-cv.toasts.success-upload'),
      );
      closeModal();
    } catch (error) {
      if (error?.status === 403) {
        toast.newToast('negative', t('modals.upload-new-cv.toasts.forbidden'));
      } else {
        toast.newToast(
          'negative',
          t('modals.upload-new-cv.toasts.something-wrong'),
        );
      }
    }
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50 update-cv-modal-container'
          onClose={isLoading ? () => null : closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>
          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel
                  className={cn(
                    'flex flex-col w-full max-w-md transform overflow-hidden',
                    'rounded bg-white p-10 text-left gap-6',
                    'align-middle shadow-xl transition-all relative',
                  )}
                >
                  <Dialog.Title
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <h1 className='text-2xl text-center font-semibold text-dark-charcoal'>
                      {t('modals.upload-new-cv.title')}
                    </h1>
                  </Dialog.Title>
                  <Dialog.Description className='text-sm text-cool-grey text-center'>
                    {t('modals.upload-new-cv.description')}
                  </Dialog.Description>
                  <Formik
                    initialValues={{ cvFile: null }}
                    validationSchema={Yup.object({
                      cvFile: Yup.mixed()
                        .required(
                          t('modals.upload-new-cv.errors.required-file'),
                        )
                        .test(
                          'fileType',
                          t('modals.upload-new-cv.errors.wrong-file-type'),
                          (value) => value?.type === 'application/pdf',
                        ),
                    })}
                    onSubmit={(values) => {
                      handleUpload(values.cvFile);
                    }}
                  >
                    {({ setFieldValue, isSubmitting }) => (
                      <Form
                        className='flex flex-col gap-6'
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className='flex flex-col gap-1'>
                          <Field name='cvFile'>
                            {({ field, form }) => (
                              <CVModal
                                onFileUpload={(uploadedFile) => {
                                  form.setFieldValue('cvFile', uploadedFile);
                                  form.setFieldTouched('cvFile', true, false);
                                }}
                                hasError={
                                  form.touched.cvFile && form.errors.cvFile
                                }
                                value={field.value}
                              />
                            )}
                          </Field>
                          <div className='min-h-[1.25rem] flex items-center'>
                            <ErrorMessage
                              name='cvFile'
                              component='p'
                              className='error text-bittersweet-shimmer text-xs font-semibold'
                            />
                          </div>
                        </div>
                        <div className='w-full flex items-center justify-center gap-2'>
                          <Button
                            type='button'
                            variant='secondary'
                            onClick={closeModal}
                          >
                            {t('modals.upload-new-cv.buttons.cancel')}
                          </Button>
                          <Button
                            type='submit'
                            variant='primary'
                            disabled={isSubmitting}
                          >
                            {t('modals.upload-new-cv.buttons.upload')}
                            <Right size='16px' color='#FFFFFF' />
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                  {isLoading && <Loader />}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
};

export default UpdateCVModal;
